import type { JSX } from "react";
import type { Action } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";
import { extractMcpGoal, extractMcpQueries } from "./mcpAudit";
import McpQueriesBlock from "./McpQueriesBlock";

interface TurnActionsTabProps {
  actions: Action[];
  actionHref: (actionId: string) => string;
  onOpenAction: (actionId: string) => void;
}

function ActionCardHeader({ action }: { action: Action }): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center space-x-3">
        <div>
          <div className="flex items-center space-x-2">
            <h4 className="text-lg font-medium text-gray-900">{action.action_type}</h4>
            {action.is_decision && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                Agent Decision
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{formatDateWithZone(action.created_at)}</p>
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm text-gray-500">{action.llm_request_count || 0} LLM requests</div>
        {(action.total_input_cost_usd || action.total_output_cost_usd) && (
          <div className="text-sm font-medium text-green-700">
            {formatCost(getTotalCostFromApi(action.total_input_cost_usd, action.total_output_cost_usd))}
          </div>
        )}
      </div>
    </div>
  );
}

function DecisionInfo({ action }: { action: Action }): JSX.Element | null {
  if (!action.is_decision || !(action.selected_action || action.resolved_action)) return null;
  return (
    <div className="mb-3 p-3 bg-white border border-purple-200 rounded">
      <label className="block text-xs font-medium text-purple-700 mb-1">Decision</label>
      <div className="flex items-center space-x-2 text-sm">
        <span className="font-mono text-purple-900">{action.selected_action}</span>
        {action.resolved_action && action.resolved_action !== action.selected_action && (
          <>
            <span className="text-purple-500">→</span>
            <span className="font-mono text-purple-900">{action.resolved_action}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Keeps the card's own onClick from also firing when the details link is used. */
export function stopClickThrough(e: { stopPropagation: () => void }): void {
  e.stopPropagation();
}

/** Keyboard activation for click-only elements: Enter/Space triggers the action. */
export function activateOnEnterOrSpace(
  onActivate: () => void,
): (e: { key: string; preventDefault: () => void }) => void {
  return (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };
}

function ActionCard({
  action,
  href,
  onOpen,
}: {
  action: Action;
  href: string;
  onOpen: () => void;
}): JSX.Element {
  return (
    <div
      className={`border rounded-lg p-4 hover:bg-gray-50 cursor-pointer ${
        action.is_decision ? "border-purple-300 bg-purple-50/30" : "border-gray-200"
      }`}
      onClick={onOpen}
      onKeyDown={activateOnEnterOrSpace(onOpen)}
      role="button"
      tabIndex={0}
    >
      <ActionCardHeader action={action} />
      <DecisionInfo action={action} />
      {action.rationale && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Rationale</label>
          <p className="text-sm text-gray-700">{action.rationale}</p>
        </div>
      )}
      <McpQueriesBlock
        goal={extractMcpGoal(action.action_output)}
        queries={extractMcpQueries(action.action_output)}
      />
      <div className="flex justify-end">
        <a
          href={href}
          onClick={stopClickThrough}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          View Details →
        </a>
      </div>
    </div>
  );
}

export default function TurnActionsTab({
  actions,
  actionHref,
  onOpenAction,
}: TurnActionsTabProps): JSX.Element {
  return (
    <div className="space-y-4">
      {actions.map((action) => (
        <ActionCard
          key={action.action_id}
          action={action}
          href={actionHref(action.action_id)}
          onOpen={onOpenAction.bind(null, action.action_id)}
        />
      ))}
      {actions.length === 0 && (
        <div className="text-center py-8 text-gray-500">No actions found for this turn.</div>
      )}
    </div>
  );
}
